// utils/apiUtils.js - Centralized API utilities with token expiration handling

import api from '../services/api'

/**
 * Wrapper for API calls that ensures proper error handling and token refresh
 * @param {Function} apiCall - The API call function to execute
 * @param {Object} options - Additional options
 * @returns {Promise} - The API response
 */
export const safeApiCall = async (apiCall, options = {}) => {
  try {
    const response = await apiCall()
    return response
  } catch (error) {
    // Handle specific error cases
    if (error.response?.status === 401) {
      // Token expired - the API interceptor will handle redirect
      throw new Error('Session expired. Please log in again.')
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to perform this action.')
    } else if (error.response?.status === 404) {
      throw new Error('The requested resource was not found.')
    } else if (error.response?.status === 422) {
      // Validation errors
      const errorMessage = error.response.data.message || 'Invalid data provided.'
      throw new Error(errorMessage)
    } else if (error.response?.status === 429) {
      throw new Error('Too many requests. Please try again later.')
    } else if (error.response?.status === 500) {
      throw new Error('Server error occurred. Please try again later.')
    } else if (error.response?.status === 503) {
      throw new Error('Service temporarily unavailable. Please try again later.')
    } else if (error.request) {
      // Network error
      throw new Error('Network error. Please check your internet connection.')
    } else {
      // Other error
      throw new Error(error.message || 'An unexpected error occurred. Please try again.')
    }
  }
}

/**
 * GET request wrapper
 * @param {string} url - The API endpoint
 * @param {Object} config - Axios config
 * @returns {Promise} - The API response
 */
export const safeGet = (url, config = {}) => {
  return safeApiCall(() => api.get(url, config))
}

/**
 * POST request wrapper
 * @param {string} url - The API endpoint
 * @param {Object} data - Request data
 * @param {Object} config - Axios config
 * @returns {Promise} - The API response
 */
export const safePost = (url, data = {}, config = {}) => {
  return safeApiCall(() => api.post(url, data, config))
}

/**
 * PUT request wrapper
 * @param {string} url - The API endpoint
 * @param {Object} data - Request data
 * @param {Object} config - Axios config
 * @returns {Promise} - The API response
 */
export const safePut = (url, data = {}, config = {}) => {
  return safeApiCall(() => api.put(url, data, config))
}

/**
 * DELETE request wrapper
 * @param {string} url - The API endpoint
 * @param {Object} config - Axios config
 * @returns {Promise} - The API response
 */
export const safeDelete = (url, config = {}) => {
  return safeApiCall(() => api.delete(url, config))
}

/**
 * PATCH request wrapper
 * @param {string} url - The API endpoint
 * @param {Object} data - Request data
 * @param {Object} config - Axios config
 * @returns {Promise} - The API response
 */
export const safePatch = (url, data = {}, config = {}) => {
  return safeApiCall(() => api.patch(url, data, config))
}

/**
 * Upload file wrapper
 * @param {string} url - The API endpoint
 * @param {FormData} formData - Form data with file
 * @param {Object} config - Axios config
 * @returns {Promise} - The API response
 */
export const safeUpload = (url, formData, config = {}) => {
  const uploadConfig = {
    ...config,
    headers: {
      'Content-Type': 'multipart/form-data',
      ...config.headers
    }
  }
  return safeApiCall(() => api.post(url, formData, uploadConfig))
}

/**
 * Check if user is authenticated
 * @returns {boolean} - True if authenticated
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('access_token')
  const userData = localStorage.getItem('user_data')
  
  if (!token || !userData || userData === 'undefined' || userData === 'null') {
    return false
  }
  
  try {
    const parsed = JSON.parse(userData)
    return !!(parsed && typeof parsed === 'object' && (parsed.id || parsed.user_id))
  } catch (error) {
    console.error('Error parsing user data:', error)
    return false
  }
}

/**
 * Get current user data
 * @returns {Object|null} - User data or null
 */
export const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem('user_data')
    if (!userData || userData === 'undefined' || userData === 'null') {
      return null
    }
    const parsed = JSON.parse(userData)
    return (parsed && typeof parsed === 'object' && (parsed.id || parsed.user_id)) ? parsed : null
  } catch (error) {
    console.error('Error parsing user data:', error)
    return null
  }
}

/**
 * Clear authentication data and redirect to login
 */
export const clearAuthAndRedirect = () => {
  const authKeys = [
    'access_token',
    'refresh_token',
    'user_data',
    'device_id',
    'verification_data',
    'verification_id',
    'intended_route'
  ]
  
  authKeys.forEach(key => {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.warn(`Failed to remove ${key}:`, error)
    }
  })
  
  // Only redirect if not already on login page to prevent loops
  if (!window.location.pathname.includes('/auth/login') && !window.location.pathname.includes('/login')) {
    // Use replace to prevent back button issues
    window.location.replace('/auth/login')
  }
}

/**
 * Handle API error with user-friendly messages
 * @param {Error} error - The error object
 * @param {Function} setError - Function to set error state
 * @param {Function} onAuthError - Optional callback for auth errors
 */
export const handleApiError = (error, setError, onAuthError = null) => {
  console.error('API Error:', error)
  
  if (error.message.includes('Session expired') || error.message.includes('Authentication required')) {
    if (onAuthError) {
      onAuthError(error)
    } else {
      setError('Session expired. Please log in again.')
    }
  } else {
    setError(error.message)
  }
}

/**
 * Retry API call with exponential backoff
 * @param {Function} apiCall - The API call function
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} baseDelay - Base delay in milliseconds
 * @returns {Promise} - The API response
 */
export const retryApiCall = async (apiCall, maxRetries = 3, baseDelay = 1000) => {
  let lastError
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await apiCall()
    } catch (error) {
      lastError = error
      
      // Don't retry on auth errors or client errors (4xx)
      if (error.response?.status >= 400 && error.response?.status < 500) {
        throw error
      }
      
      // Don't retry on the last attempt
      if (attempt === maxRetries) {
        throw error
      }
      
      // Calculate delay with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt)
      
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
  
  throw lastError
}

export default {
  safeApiCall,
  safeGet,
  safePost,
  safePut,
  safeDelete,
  safePatch,
  safeUpload,
  isAuthenticated,
  getCurrentUser,
  clearAuthAndRedirect,
  handleApiError,
  retryApiCall
}
